import { lazy } from 'react';
import type { ComponentType } from 'react';
import type { AgenticViewerProps } from '@amzn/work-spaces-agentic-web-viewer';
import { errorNameOf } from './errorNameOf';

export class ViewerChunkLoadError extends Error {
  readonly causeName: string;

  constructor(causeName: string) {
    super('The agentic viewer chunk failed to load');
    this.name = 'ViewerChunkLoadError';
    this.causeName = causeName;
  }
}

// Webpack names a failed script chunk ChunkLoadError, and mini-css-extract-plugin
// gives a failed CSS chunk this code. Any other error is the viewer module's own,
// which a reload would meet again.
const isChunkLoadError = (error: unknown): boolean =>
  error instanceof Error &&
  (error.name === 'ChunkLoadError' ||
    (error as { code?: unknown }).code === 'CSS_CHUNK_LOAD_FAILED');

// A failed preload is dropped: the route's own load meets the same failure and
// raises it into the route boundary.
export const preloadAgenticViewer = (): void => {
  import('./agenticViewerModule').catch(() => undefined);
};

export const loadAgenticViewer = async (): Promise<{
  default: ComponentType<AgenticViewerProps>;
}> => {
  try {
    const { AgenticViewer } = await import('./agenticViewerModule');
    return { default: AgenticViewer };
  } catch (error) {
    if (!isChunkLoadError(error)) {
      throw error;
    }
    throw new ViewerChunkLoadError(errorNameOf(error));
  }
};

export const LazyAgenticViewer = lazy(loadAgenticViewer);
